import { useEffect, useState, useCallback } from 'react';
import { AdministratorRole, User, useUpdateUser } from '../../../hooks/useUsers';
import { useDispatch, connect } from 'react-redux';
import debounce from '../../../components/common/debounce';
import { fetchEmailAvailableRequest } from '../../../components/account-login/login-redux/actions';
import { CustomInput, DropDown, ToggleButton } from '../../../components/common';
import Model from '../../../components/common/model';
import AlertBox from '../../../components/route-leaving-prompt/alert-box';
import { useDidMountEffect } from '../../../components/custom-hook';
import {
  normalizeMobilePhone,
  validateMobilePhone,
} from '../../../components/common/phone-validation';

type props = {
  user: User;
  emailAvailable: any;
  appSettings: any;
};

const userStatus = {
  enabled: 'Enabled',
  disabled: 'Disabled',
  under_review: 'Under Review',
  suspended: 'Suspended',
  banned: 'Banned',
};

const EditUserDetails: React.FunctionComponent<props> = ({
  user,
  emailAvailable,
  appSettings,
}): JSX.Element => {
  const { updateUser } = useUpdateUser(user.id);
  const [modal, setModal] = useState<boolean>(false);
  const [alertModal, setAlertModal] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState<User>(user);
  const [errorMessages, setErrorMessages] = useState<any>({});
  const [isAvailableEmail, setIsAvailableEmail] = useState<boolean>(true);
  const [timeZones, setTimeZones] = useState<string[]>([]);
  const [enableSaveOption, setEnableSaveOption] = useState(false);

  const dispatch = useDispatch();
  const emailDebounce = debounce(user?.email_address, 500);

  useDidMountEffect(() => {
    if (userDetails?.email_address !== user?.email_address)
      dispatch(fetchEmailAvailableRequest({ email: userDetails?.email_address }));
  }, [emailDebounce]);

  const setDefaultOptions = useCallback(() => {
    const {
      mobile_phone,
      first_name,
      last_name,
      display_name,
      gender,
      timezone,
      email_address,
      administrator_role,
    } = user;
    const mobilePhoneInput = mobile_phone.replace('+1', '');
    setUserDetails((user) => ({
      ...user,
      mobile_phone: normalizeMobilePhone(mobilePhoneInput),
      first_name,
      last_name,
      display_name,
      gender,
      timezone,
      email_address,
      administrator_role,
    }));
    return userDetails;
  }, [user]);

  const updateUserDetails = (name: string, value: string | number | AdministratorRole | null) => {
    setEnableSaveOption(true);
    setUserDetails((prev: any) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (appSettings) {
      const app = appSettings?.data?.app;
      setTimeZones(app.supported_timezones);
    }
  }, [appSettings]);

  useEffect(() => {
    if (emailAvailable && userDetails && user) {
      setIsAvailableEmail(emailAvailable?.data);

      const { email_address } = userDetails;

      const emailRegex = /\S+@\S+\.\S+/;

      if (!emailRegex.test(email_address) || email_address === '') {
        return setErrorMessages({ email_address: 'Enter valid email address' });
      } else if (!emailAvailable?.data && user.email_address !== email_address) {
        setErrorMessages({ email_address: 'Email already used, use different email' });
      } else if (emailAvailable?.data) {
        setErrorMessages({});
      }
    }
  }, [emailAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateUserDetails = async () => {
    let isValid = false;
    try {
      validateEmail();
      const { mobile_phone } = userDetails;
      const phoneErrorMessage = await validateMobilePhone(mobile_phone); // extract a return val from this guy
      if (phoneErrorMessage !== '') {
        setErrorMessages({ mobile_phone: phoneErrorMessage });
      } else if (phoneErrorMessage === '' && validateEmail() === true) {
        setErrorMessages({});
        isValid = true;
      }
    } catch (e) {}

    return isValid;
  };

  const validateEmail = () => {
    const { email_address } = userDetails;

    const emailRegex = /\S+@\S+\.\S+/;

    if (!emailRegex.test(email_address) || email_address === '') {
      return setErrorMessages({ email_address: 'Enter valid email address' });
    } else if (isAvailableEmail === false && user.email_address !== email_address) {
      return setErrorMessages({
        email_address: 'Email already used, use different email',
      });
    }

    setErrorMessages({});

    return true;
  };

  const handleSave = async () => {
    const payload = {
      ...userDetails,
    };

    if (await validateUserDetails()) {
      updateUser.mutate(payload);
      setModal(false);
      setAlertModal(false);
      setEnableSaveOption(false);
    }
  };

  useEffect(() => {
    if (modal && user) {
      setDefaultOptions();
    }
  }, [modal, user, setDefaultOptions]);

  return (
    <>
      <button
        onClick={(event) => {
          event.stopPropagation();
          setModal(true);
        }}
        className=" text-center h-10 text-sm text-white px-2 bg-blue-primary rounded-sm "
      >
        Edit
      </button>
      <Model
        isModel={modal}
        InnerComponent={
          <div
            onClick={(event) => {
              event.stopPropagation();
            }}
            className="inline-block align-bottom bg-white overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full"
          >
            <div className="bg-white px-2 pt-3 pb-2 sm:p-6 sm:pb-4">
              <div className="flex justify-start ">
                <div>
                  <h3 className="text-2xl font-normal blue-primary  py-1">Edit User</h3>
                </div>
              </div>
              <div className="py-2 flex flex-start">
                <h3 className="text-lg  font-bold text-gray-dark  py-1">Details</h3>
              </div>
              <div className="grid grid-cols-3 gap-3 gap-y-5 mr-5">
                <div>
                  <CustomInput
                    inputType={'text'}
                    value={userDetails?.first_name}
                    setValue={(data) => updateUserDetails('first_name', data)}
                    Label={'First Name'}
                  />
                </div>
                <div>
                  <CustomInput
                    inputType={'text'}
                    value={userDetails?.last_name}
                    setValue={(data) => updateUserDetails('last_name', data)}
                    Label={'Last Name'}
                  />
                </div>
                <div>
                  <CustomInput
                    inputType={'text'}
                    value={userDetails?.display_name}
                    setValue={(data) => updateUserDetails('display_name', data)}
                    Label={'Display Name'}
                  />
                </div>
                <div>
                  <CustomInput
                    inputType={'text'}
                    value={userDetails?.mobile_phone}
                    setValue={(data) => updateUserDetails('mobile_phone', data)}
                    Label={'Phone'}
                    Error={errorMessages.mobile_phone?.length > 0 ? true : false}
                    ErrorMessage={errorMessages.mobile_phone}
                  />
                </div>
                <div>
                  <CustomInput
                    inputType={'text'}
                    value={userDetails?.email_address}
                    setValue={(data) => updateUserDetails('email_address', data)}
                    Label={'Email'}
                    Error={errorMessages.email_address?.length > 0 ? true : false}
                    ErrorMessage={errorMessages.email_address}
                  />
                </div>
                <div>
                  <DropDown
                    Label={'Timezone'}
                    labelClass="float-left block leading-4 text-gray-dark mb-2"
                    value={userDetails?.timezone}
                    setValue={(data) => {
                      setEnableSaveOption(true);
                      setUserDetails((prev: any) => ({
                        ...prev,
                        timezone: data === '' ? null : data,
                      }));
                    }}
                  >
                    <>
                      <option key={'timezone'} value={''}>
                        Select TimeZone
                      </option>
                      {timeZones?.length > 0 &&
                        timeZones?.map((timezone: string) => (
                          <option key={timezone} value={timezone}>
                            {timezone}
                          </option>
                        ))}
                    </>
                  </DropDown>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 gap-y-5 mr-5 mt-2">
                <div>
                  <DropDown
                    Label={'Account Status'}
                    labelClass="float-left block leading-4 text-gray-dark mb-2"
                    value={userDetails?.status}
                    setValue={(data) => {
                      updateUserDetails('status', data);
                    }}
                  >
                    <>
                      {Object.keys(userStatus).map((item: string) => (
                        <option key={item} value={item}>
                          {/* @ts-ignore */}
                          {userStatus[item]}
                        </option>
                      ))}
                    </>
                  </DropDown>
                </div>
                <div>
                  <DropDown
                    Label={'Gender'}
                    labelClass="float-left block leading-4 text-gray-dark mb-2"
                    value={userDetails?.gender}
                    setValue={(data) => updateUserDetails('gender', data)}
                  >
                    <option value={'Male'}>
                      Male
                    </option>
                    <option value={'Female'}>
                      Female
                    </option>
                    <option value={'Non Binary'}>
                      Non Binary
                    </option>
                    <option value={'Agender'}>
                      Agender
                    </option>
                    <option value={'Other'}>
                      Other
                    </option>
                    <option value={'Prefer not to say'}>
                      Prefer not to say
                    </option>
                  </DropDown>
                </div>
                <div className="mt-6 pl-4">
                  <div className="text-md text-gray-dark float-left pr-4">Administrator:</div>
                  <div className="">
                    <ToggleButton
                      id={'administratorRole'}
                      status={userDetails?.administrator_role}
                      initialstatus={false}
                      setStatus={(data:boolean) => {
                        if (data === true) {
                        updateUserDetails('administrator_role', {type: "administrator"})
                      }
                      else {
                        updateUserDetails('administrator_role', null)
                      }
                      }}
                    ></ToggleButton>
                  </div>
                </div>
              </div>
              <div className="flex justify-end mt-2">
                <div>
                  <button
                    className={
                      ' text-center h-8 text-sm text-white px-2  w-16 rounded-sm ' +
                      (enableSaveOption ? 'bg-blue-primary' : 'bg-gray-200')
                    }
                    onClick={handleSave}
                    disabled={!enableSaveOption}
                  >
                    Save
                  </button>
                  <button
                    className=" text-center h-8 text-sm text-white px-2 bg-blue-primary w-16 rounded-sm ml-4 "
                    onClick={() => {
                      if (enableSaveOption) {
                        setAlertModal(true);
                      } else {
                        setModal(false);
                      }
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        }
      />
      <AlertBox
        visible={alertModal}
        handlePrompt={() => {
          setDefaultOptions();
          setModal(false);
          setAlertModal(false);
          setErrorMessages({});
          setEnableSaveOption(false);
        }}
        handleContinueSave={handleSave}
        closeModal={() => {
          setAlertModal(false);
        }}
        confirmButtonText={'Discard Changes'}
        titleText={'Alert'}
        contentText={
          'You have unsaved changes.  If you leave this screen without saving, your changes will be lost.'
        }
        confirmSaveButtonText={'Save Changes'}
        cancelButtonText={'Cancel'}
      />
    </>
  );
};

const mapStateToProps = (state: any) => {
  return {
    emailAvailable: state.login.emailAvailable,
    appSettings: state.Tos.clientAdmin?.data || null,
  };
};

export default connect(mapStateToProps)(EditUserDetails);
